import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider } from "@/context/SiteContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteLayout } from "@/components/site/SiteLayout";

import Home from "@/pages/Home";
import Approach from "@/pages/Approach";
import Reviews from "@/pages/Reviews";
import Treatments from "@/pages/Treatments";
import TreatmentDetail from "@/pages/TreatmentDetail";
import Conditions from "@/pages/Conditions";
import ConditionDetail from "@/pages/ConditionDetail";
import FamilyCare from "@/pages/FamilyCare";
import PatientStories from "@/pages/PatientStories";
import About from "@/pages/About";
import Learn from "@/pages/Learn";
import ArticleDetail from "@/pages/ArticleDetail";
import Contact from "@/pages/Contact";
import FirstVisit from "@/pages/FirstVisit";
import Memberships from "@/pages/Memberships";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import NervousSystem from "@/pages/NervousSystem";
import Pregnancy from "@/pages/Pregnancy";
import Pediatric from "@/pages/Pediatric";
import Portal from "@/pages/Portal";
import Join from "@/pages/Join";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import HomeEditor from "@/pages/admin/HomeEditor";
import ApproachEditor from "@/pages/admin/ApproachEditor";
import SettingsEditor from "@/pages/admin/SettingsEditor";
import CollectionManager from "@/pages/admin/CollectionManager";
import MediaLibrary from "@/pages/admin/MediaLibrary";

function RequireStaff({ children }) {
  const { user, isStaff } = useAuth();
  if (user === null) return <div className="min-h-screen bg-linen flex items-center justify-center text-taupe">Loading…</div>;
  if (!isStaff) return <Navigate to="/login" replace />;
  return children;
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-linen flex items-center justify-center text-taupe">Loading…</div>;
  if (!user) return <Navigate to="/join" replace />;
  return children;
}

const Public = (el) => <SiteLayout>{el}</SiteLayout>;

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={Public(<Home />)} />
            <Route path="/approach" element={Public(<Approach />)} />
            <Route path="/the-chain" element={<Navigate to="/approach" replace />} />
            <Route path="/nervous-system" element={Public(<NervousSystem />)} />
            <Route path="/pregnancy" element={Public(<Pregnancy />)} />
            <Route path="/pediatric" element={Public(<Pediatric />)} />
            <Route path="/treatments" element={Public(<Treatments />)} />
            <Route path="/treatments/:slug" element={Public(<TreatmentDetail />)} />
            <Route path="/conditions" element={Public(<Conditions />)} />
            <Route path="/conditions/:slug" element={Public(<ConditionDetail />)} />
            <Route path="/family-care" element={Public(<FamilyCare />)} />
            <Route path="/reviews" element={Public(<Reviews />)} />
            <Route path="/patient-stories" element={Public(<PatientStories />)} />
            <Route path="/about" element={Public(<About />)} />
            <Route path="/learn" element={Public(<Learn />)} />
            <Route path="/learn/:slug" element={Public(<ArticleDetail />)} />
            <Route path="/contact" element={Public(<Contact />)} />
            <Route path="/first-visit" element={Public(<FirstVisit />)} />
            <Route path="/memberships" element={Public(<Memberships />)} />
            <Route path="/payment/success" element={Public(<PaymentSuccess />)} />
            <Route path="/payment/cancel" element={Public(<PaymentCancel />)} />
            <Route path="/privacy" element={Public(<Legal kind="privacy" />)} />
            <Route path="/terms" element={Public(<Legal kind="terms" />)} />
            <Route path="/accessibility" element={Public(<Legal kind="accessibility" />)} />

            <Route path="/login" element={<Login />} />
            <Route path="/join" element={<Join />} />
            <Route path="/portal" element={<RequireAuth><Portal /></RequireAuth>} />

            <Route path="/admin" element={<RequireStaff><AdminDashboard /></RequireStaff>} />
            <Route path="/admin/home" element={<RequireStaff><HomeEditor /></RequireStaff>} />
            <Route path="/admin/approach" element={<RequireStaff><ApproachEditor /></RequireStaff>} />
            <Route path="/admin/settings" element={<RequireStaff><SettingsEditor /></RequireStaff>} />
            <Route path="/admin/media" element={<RequireStaff><MediaLibrary /></RequireStaff>} />
            <Route path="/admin/:name" element={<RequireStaff><CollectionManager /></RequireStaff>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
